import React, { useEffect, useState } from 'react';
import { History, Loader2, ChevronDown } from 'lucide-react';
import api from '../utils/api';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '—');

// Cached per quote id so remounts while the page reloads don't refetch.
const cache = {};

const VersionRow = ({ v, onOpen }) => (
  <li>
    <button
      type="button"
      onClick={() => !v.is_current && onOpen(v.quotation_id)}
      className={`w-full min-h-[44px] text-left grid grid-cols-[auto_1fr] sm:grid-cols-[auto_1fr_auto] items-center gap-x-3 gap-y-0.5 px-3 py-2 rounded-md transition-colors ${v.is_current ? 'bg-white ring-1 ring-[var(--navy)]' : 'hover:bg-white'}`}
      aria-current={v.is_current ? 'true' : undefined}
      data-testid={`version-row-${v.version_label}`}
    >
      <span className="font-semibold text-sm text-[var(--navy)] w-9">{v.version_label}</span>
      <span className="min-w-0 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-sm text-[#374151] break-all">{v.quote_reference}</span>
        {v.is_latest && (
          <span className="text-[10px] font-semibold uppercase tracking-wide bg-green-100 text-green-700 rounded px-1.5 py-0.5" data-testid={`version-latest-badge-${v.version_label}`}>Latest</span>
        )}
        {v.is_current && <span className="text-[10px] text-[var(--navy)] font-medium">Viewing</span>}
      </span>
      <span className="col-start-2 sm:col-start-auto text-[13px] text-[#6B7280] sm:text-right">{fmt(v.created_at)} · {v.created_by || '—'}</span>
    </button>
  </li>
);

// Every version of the viewed quote; collapsed to the current row until "Show all versions".
export const VersionHistoryPanel = ({ quotationId, onOpen, onLoaded }) => {
  const [versions, setVersions] = useState(cache[quotationId] || null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let live = true;
    setOpen(false);
    if (cache[quotationId]) {
      setVersions(cache[quotationId]);
      onLoaded?.(cache[quotationId]);
    } else {
      setVersions(null);
    }
    api.get(`/quotations/${quotationId}/versions`)
      .then((r) => {
        cache[quotationId] = r.data.versions || [];
        if (live) { setVersions(cache[quotationId]); onLoaded?.(cache[quotationId]); }
      })
      .catch(() => live && setVersions([]));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotationId]);

  const shown = open ? versions || [] : (versions || []).filter((v) => v.is_current);
  return (
    <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-[8px] p-4 print:hidden" data-testid="version-history-panel">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-2">
        <History className="w-[18px] h-[18px] text-[var(--navy)]" />
        <h2 className="text-base font-semibold text-[var(--navy)]">Version History</h2>
        <span className="text-[13px] text-[#6B7280]">Saved versions are read-only</span>
        {versions?.length > 1 && (
          <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
            className="ml-auto min-h-[44px] inline-flex items-center gap-1 text-sm font-medium text-[var(--navy)] hover:underline"
            data-testid="version-toggle-btn">
            {open ? 'Hide versions' : `Show all versions (${versions.length})`}
            <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>
      {versions === null ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        : <ul className="space-y-1">{shown.map((v) => <VersionRow key={v.quotation_id} v={v} onOpen={onOpen} />)}</ul>}
    </div>
  );
};
